import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiArrowRight, FiCheck, FiStar, FiTrendingUp, FiUsers, FiZap, FiTarget, FiHeart, FiMapPin, FiCompass } = FiIcons;

function App() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const questions = [
    {
      id: 'q1',
      title: 'あなたが今、最も力を入れていることはどの分野ですか？',
      icon: FiTarget,
      options: [
        { value: 'strengths', label: '自分の強みを明確にすること', step: 1 },
        { value: 'products', label: '商品・サービスを整えること', step: 2 },
        { value: 'automation', label: '自動化の仕組みを作ること', step: 3 },
        { value: 'marketing', label: 'SNSや広告で集客を強化すること', step: 4 },
        { value: 'relationships', label: 'お客様との関係を長く続けること', step: 5 }
      ]
    },
    {
      id: 'q2',
      title: 'セッション後、どんな未来が一番ワクワクしますか？',
      icon: FiStar,
      options: [
        { value: 'ai_system', label: 'AIが24時間働く仕組みが完成する', program: 'sales' },
        { value: 'ai_products', label: '自分のサービスがAIで商品化される', program: 'website' },
        { value: 'ai_content', label: 'SNSで共感される発信が自然にできる', program: 'marketing' }
      ]
    },
    {
      id: 'q3',
      title: 'AIとの関わり方で、一番ピンとくるのは？',
      icon: FiZap,
      options: [
        { value: 'partner', label: '私がAIを導くパートナーのような関係', type: 'leader' },
        { value: 'delegate', label: 'AIに任せて、自分はクリエイティブに集中したい', type: 'creative' },
        { value: 'guided', label: 'AIに導かれながら、新しい分野を開拓していきたい', type: 'explorer' }
      ]
    },
    {
      id: 'q4',
      title: 'あなたにとって今の一番の"壁"はどれですか？',
      icon: FiTrendingUp,
      options: [
        { value: 'construction', label: '構築（仕組みづくり）', challenge: 'build' },
        { value: 'implementation', label: '実装（実際の導入・運用）', challenge: 'execute' },
        { value: 'continuation', label: '継続（習慣化・発信）', challenge: 'sustain' }
      ]
    },
    {
      id: 'q5',
      title: 'この診断を終えて、AIにどんなサポートをしてもらいたいと感じましたか？',
      icon: FiHeart,
      options: [
        { value: 'direction', label: '自分に合ったビジネスの方向性を具体化したい', program: 'website' },
        { value: 'mechanism', label: 'AIを使った集客や販売の仕組みを作りたい', program: 'sales' },
        { value: 'sustainable', label: '継続的にAIを活用して安定収益を得たい', program: 'marketing' },
        { value: 'other', label: 'その他（自由記述）', type: 'text' }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    if (currentQuestion < questions.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      setTimeout(() => {
        setShowResult(true);
      }, 500);
    }
  };

  const handleTextAnswer = (questionId, text) => {
    setAnswers(prev => ({ ...prev, [questionId]: text }));
    setTimeout(() => {
      setShowResult(true);
    }, 500);
  };

  const calculateResult = () => {
    const q1Answer = answers.q1;
    const q2Answer = answers.q2;
    const q4Answer = answers.q4;
    const q5Answer = answers.q5;

    let currentStep = 1;
    let recommendedProgram = 'sales';
    let stepTitle = '';
    let recommendation = '';
    let aiMessage = '';

    // Determine current step based on Q1
    if (q1Answer) {
      currentStep = q1Answer.step;
    }

    // Step titles
    const stepTitles = {
      1: '強み発見フェーズ',
      2: '商品構築フェーズ', 
      3: '自動化構築フェーズ',
      4: '集客強化フェーズ',
      5: '関係継続フェーズ'
    };

    stepTitle = stepTitles[currentStep];

    // Prioritize Q5 answer for program recommendation
    if (q5Answer && q5Answer.program) {
      recommendedProgram = q5Answer.program;
    } else if (q2Answer && q2Answer.program) {
      recommendedProgram = q2Answer.program;
    }

    // Generate recommendations
    if (recommendedProgram === 'sales') {
      recommendation = 'あなたの強みをAIに語らせる仕組みを整える時期です。AI台本とスライドがあなたの代弁者になります。AIが24時間働く自動セールス構造を構築し、「書けない・作れない」苦痛から解放されましょう。';
    } else if (recommendedProgram === 'website') {
      recommendation = 'あなたのアイデアを"形"にするフェーズです。AIサイト構築で、信頼を可視化しましょう。コーディング不要でプロ品質のWEB制作スキルを身につけ、「AIサイト制作者」としての新しい働き方を確立できます。';
    } else if (recommendedProgram === 'marketing') {
      recommendation = 'あなたの世界観を発信する準備が整っています。AIアシスタントと共に、継続発信の習慣を作りましょう。「続かない」を「AIが支える」に変える仕組みで、安定した集客基盤を築けます。';
    }

    aiMessage = 'あなたが描いたAIビジネスの地図は、もう動き始めています。AIはあなたの努力を形にする最高の相棒です。この診断で見えた方向性を信じて、一歩ずつ前進していきましょう。';

    return {
      currentStep,
      stepTitle,
      recommendation,
      aiMessage,
      recommendedProgram
    };
  };

  const result = showResult ? calculateResult() : null;

  if (showResult && result) {
    return (
      <motion.div 
        className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container mx-auto px-4 py-12">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full mb-6 shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <SafeIcon icon={FiCompass} className="text-3xl text-white" />
              </motion.div>
              <h1 className="text-4xl font-bold text-white mb-4">
                あなた専用のAIビジネス・ロードマップ
              </h1>
              <p className="text-lg text-gray-300">
                診断結果：ウェビナーで得た"気づき"をAIが可視化しました
              </p>
            </div>

            {/* Result Card */}
            <motion.div
              className="bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-amber-500/20"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {/* Golden Header */}
              <div className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 p-8 text-white relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{ x: [-100, 400] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                />
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-4">
                    <SafeIcon icon={FiMapPin} className="text-2xl mr-3" />
                    <h2 className="text-2xl font-bold">あなたの現在地</h2>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold mb-2">STEP {result.currentStep}</div>
                    <div className="text-xl font-medium">{result.stepTitle}</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 space-y-8">
                {/* Diagnosis Summary */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-600/20 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUsers} className="text-xl text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-3">診断の解説</h3>
                      <p className="text-gray-300 leading-relaxed">
                        あなたは現在{result.stepTitle}にいらっしゃいます。ウェビナーでの気づきと今回の回答から、
                        AIビジネスの地図上での立ち位置が明確になりました。
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Recommendation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-600/20 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiTarget} className="text-xl text-green-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-3">次のステップに向けたAIからの提案</h3>
                      <p className="text-gray-300 leading-relaxed">
                        {result.recommendation}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* AI Message */}
                <motion.div
                  className="bg-gradient-to-r from-amber-600/20 to-yellow-600/20 rounded-2xl p-6 border border-amber-500/30"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-600/30 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiHeart} className="text-xl text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-3">AIからのメッセージ</h3>
                      <p className="text-gray-200 leading-relaxed font-medium">
                        {result.aiMessage}
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* User's Free Answer */}
                {answers.q5 && (answers.q5.value === 'other' || typeof answers.q5 === 'string') && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.4 }}
                  >
                    <div className="bg-gray-700/50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-white mb-3">あなたからのメッセージ</h3>
                      <p className="text-gray-300 italic">
                        「{typeof answers.q5 === 'string' ? answers.q5 : answers.q5.label}」
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Action Button */}
                <motion.div
                  className="text-center pt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.6 }}
                >
                  <button
                    onClick={() => {
                      setShowResult(false);
                      setCurrentQuestion(0);
                      setAnswers({});
                    }}
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    <SafeIcon icon={FiArrowRight} className="mr-2" />
                    もう一度診断する
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          {currentQuestion === 0 && (
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6 shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <SafeIcon icon={FiCompass} className="text-3xl text-white" />
              </motion.div>
              <h1 className="text-4xl font-bold text-white mb-4">
                あなた専用のAIビジネス・ロードマップ診断
              </h1>
              <p className="text-xl text-amber-400 mb-8 font-medium">
                ウェビナーで得た"気づき"をAIと一緒に整理・可視化します
              </p>
              <motion.div
                className="bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-700 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <p className="text-gray-300 leading-relaxed">
                  あなたのAIビジネスの旅は、いま始まったばかりです。<br />
                  これからの5つの質問を通して、AIがあなたの"現在地"と"次のステップ"を見える化します。<br />
                  ウェビナーで感じたひらめきや疑問を、AIが整理しながら次の行動プランへと導きます。
                </p>
              </motion.div>
            </motion.div>
          )}

          {/* Progress Bar */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-400">
                質問 {currentQuestion + 1} / {questions.length}
              </span>
              <span className="text-sm font-medium text-amber-400">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              className="bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-gray-700"
              initial={{ opacity: 0, x: isAnimating ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isAnimating ? 50 : -50 }}
              transition={{ duration: 0.4 }}
            >
              {/* Question Header */}
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
                <div className="flex items-center justify-center mb-4">
                  <SafeIcon icon={currentQ.icon} className="text-3xl mr-3" />
                  <h2 className="text-xl font-bold">Q{currentQuestion + 1}</h2>
                </div>
                <h3 className="text-lg text-center font-medium leading-relaxed">
                  {currentQ.title}
                </h3>
              </div>

              {/* Question Content */}
              <div className="p-8">
                {currentQ.options && currentQ.options.some(opt => opt.type === 'text') ? (
                  <TextInput
                    onSubmit={(text) => handleTextAnswer(currentQ.id, text)}
                  />
                ) : (
                  <div className="space-y-4">
                    {currentQ.options && currentQ.options.map((option, index) => (
                      <motion.button
                        key={option.value}
                        className="w-full p-4 text-left bg-gray-700 hover:bg-gray-600 rounded-xl border border-gray-600 hover:border-blue-500 transition-all duration-300 group"
                        onClick={() => handleAnswer(currentQ.id, option)}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-8 h-8 bg-gray-600 border-2 border-gray-500 rounded-full flex items-center justify-center group-hover:border-blue-500 transition-colors">
                            <SafeIcon icon={FiCheck} className="text-transparent group-hover:text-blue-400 transition-colors" />
                          </div>
                          <span className="ml-4 text-gray-300 group-hover:text-blue-300 font-medium">
                            {option.label}
                          </span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function TextInput({ onSubmit }) {
  const [text, setText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (text.trim()) {
      onSubmit(text.trim());
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="具体的にご記入ください..."
          className="w-full h-32 p-4 bg-gray-700 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-gray-200 placeholder-gray-400"
          required
        />
      </div>
      <div className="text-center">
        <button
          type="submit"
          disabled={!text.trim()}
          className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          <SafeIcon icon={FiArrowRight} className="mr-2" />
          診断結果を見る
        </button>
      </div>
    </motion.form>
  );
}

export default App;